import React, { CSSProperties, useState } from 'react';
import { Card } from '../../Card';
import { Loader } from '../../Loader';
import { ErrorBoundary } from '../../../containers';
import { formatMessageHelper, FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { injectIntl } from 'react-intl';
import { WrappedComponentProps as InjectedIntlProps } from 'react-intl/src/components/injectIntl';
import { addModifiers, formatDate } from '../../../shared/helpers';
import { getColumnTransform } from '../../../containers/DataExplorer/DataTableCommon/DataTableCommon';
import {
  CellContext,
  Column,
  ColumnPinningState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { AggregationFnOption } from '@tanstack/table-core/src/features/Grouping';
import { getHeaderClassName } from '../../Learn/LearnBlocks/BlockTable';
import { getFooterGroups } from '../../../shared/table/table-helpers';
import { ColumnDef } from '@tanstack/table-core/src/types';

import './AlphaPyramids.scss';
import { Popup } from 'semantic-ui-react';
import { getDatasetsPath } from '../../../containers/DataExplorer/dataExplorerRouterPaths';
import { AlphaPyramid, DelayByRegion, useAlphaPyramid, useConsultantPerformance } from '../../../shared/api/apiHooks';
import moment from 'moment';
import { GeniusQuarterHistory } from '../../../shared/api/api';
import { useSelector } from 'react-redux';
import { alphasLoadedOptionsSelector, alphasOptionsSettingsChildrenSelector } from '../../../reducers/AlphasReducer/AlphasSelector';
import Immutable from 'immutable';
import { geniusSelectedQuarterSelector } from '../../../reducers/GeniusProgramReducer/GeniusSelectors';
import analytics from '../../../shared/services/analytics/analytics';

export function getQuarterLimits(quarterPerformance: GeniusQuarterHistory | null) {
  const startDate = quarterPerformance?.quarter.startDate ?? moment().startOf('quarter').format('YYYY-MM-DD');
  const endDate = moment(quarterPerformance?.quarter.endDate ?? moment().endOf('quarter'))
    .add(1, 'day')
    .format('YYYY-MM-DD');
  return { startDate, endDate };
}

function AlphaDistributionWithoutIntl({ intl }: InjectedIntlProps) {
  const { isFetching } = useConsultantPerformance();
  const selectedQuarter = useSelector(geniusSelectedQuarterSelector);

  if (isFetching) {
    return (
      <div className="loader">
        <Loader active={true} inline="centered" size="massive" />
      </div>
    );
  }

  // `endDate` in Diversity Range API is not included, we have to increase it by one day
  const { startDate, endDate } = getQuarterLimits(selectedQuarter);

  return (
    <>
      <div className="research-paradigm__header">
        <h2 className="genius__subtitle">{formatMessageHelper(intl, 'genius.alphaPyramids.title')}</h2>
        {selectedQuarter && (
          <small className={addModifiers('genius__hint', ['dark'])}>
            <FormattedMessageHelper
              id="genius.quarter"
              values={{
                name: selectedQuarter.quarter.name,
                startDate: formatDate(moment(selectedQuarter.quarter.startDate)),
                endDate: formatDate(moment(selectedQuarter.quarter.endDate)),
              }}
            />
          </small>
        )}
      </div>
      <Card hasHeader={false}>
        <ErrorBoundary isNotFullScreen={true}>
          <PyramidsTable intl={intl} startDate={startDate} endDate={endDate} />
        </ErrorBoundary>
      </Card>
    </>
  );
}

export const AlphaPyramids = React.memo(injectIntl(AlphaDistributionWithoutIntl));

export interface PyramidColumn<Data> {
  id: string;
  name: string;
  active?: boolean;
  cell?: (props: CellContext<Data, Data>) => JSX.Element;
  accessor?: string | ((data: Data) => any);
  accessorFn?: (data: Data) => any;
  readOnly?: boolean;
  enableSorting?: boolean;
  display?: boolean;
  type?: string;
  tab?: string[];
  size?: number;
  alignHeader?: 'left' | 'right';
  columns?: PyramidColumn<Data>[];
  aggregated?: (row: Row<Data>) => JSX.Element;
  aggregatedCell?: (props: CellContext<Data, Data>) => JSX.Element;
  aggregationFn?: AggregationFnOption<Data>;
  enableGrouping?: boolean;
  getGroupingValue?: (row: Row<Data>) => any;
  isColumnLocalized?: boolean;
}

interface TooltipProps {
  count: number;
  region?: string;
  delay: number;
  category: string;
}

function TooltipContent({ count, region, delay, category }: TooltipProps) {
  const status = count >= activationThreshold ? 'active' : 'inactive';
  return (
    <div className="alpha-pyramids-tooltip">
      <div>
        {region}/D{delay}/{category}
      </div>
      <div>
        <FormattedMessageHelper id="genius.alphaPyramids.tooltip.submissions" values={{ count }} />
      </div>
      <div>
        <FormattedMessageHelper id={`genius.alphaPyramids.tooltip.${status}`} />
      </div>
      <div className="alpha-pyramids-tooltip__hint">
        <FormattedMessageHelper id="genius.alphaPyramids.tooltip.hint" />
      </div>
    </div>
  );
}

function getCellModifierByCount(alphaCount: number) {
  if (alphaCount >= activationThreshold) {
    return 'active';
  }
  if (alphaCount === 0) {
    return 'zero';
  }
  return 'non-zero';
}

const getCommonPinningStyles = (column: Column<any>): CSSProperties => {
  const isPinned = column.getIsPinned();

  return {
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    zIndex: isPinned ? 1 : 0,
  };
};

export function getReactTableColumns(
  regionDelays: DelayByRegion[],
  defaultUniversesByRegion: Record<string, string>,
  onCellClick?: (scope: string) => void
): PyramidColumn<AlphaPyramid>[] {
  // @ts-ignore
  return (
    [
      {
        id: 'dataCategory',
        name: 'genius.alphaPyramids.category',
        accessor: (pyramid: AlphaPyramid) => pyramid.category.id,
        active: true,
        display: true,
        type: 'choices',
        size: 120,
        cell: cellContext => {
          if (cellContext.row.original?.category?.name) {
            return (
              <div className={addModifiers('alphas-list-table__cell-content', ['with-wrapping'])}>
                {cellContext.row.original.category.name}
              </div>
            );
          }

          return <span />;
        },
      },
    ] as PyramidColumn<AlphaPyramid>[]
  ).concat(
    regionDelays.map(delaysByRegion => ({
      id: delaysByRegion.region,
      name: delaysByRegion.region,
      isHeaderLocalized: false,
      accessor: (pyramid: AlphaPyramid) => pyramid.regions,
      active: true,
      display: true,
      type: 'choices',
      isColumnLocalized: false,
      columns: delaysByRegion.delay.map(delay => ({
        id: `${delaysByRegion.region}_${delay}`,
        name: delay.toString(),
        isHeaderLocalized: false,
        accessor: (pyramid: AlphaPyramid) => pyramid.regions[`${delaysByRegion.region}_${delay}`]?.alphaCount,
        active: true,
        display: true,
        type: 'choices',
        size: 16,

        cell: cellContext => {
          const stats = cellContext.row.original.regions[`${delaysByRegion.region}_${delay}`];
          if (stats) {
            return (
              <Popup
                content={
                  <TooltipContent
                    region={delaysByRegion.region}
                    delay={delay}
                    category={cellContext.row.original.category.name}
                    count={stats.alphaCount}
                  />
                }
                on={'hover'}
                trigger={
                  <a
                    className="alpha-pyramids-cell__link"
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => onCellClick?.(`${delaysByRegion.region}/D${delay}/${cellContext.row.original.category.id}`)}
                    href={getDatasetsPath({
                      queryParams: {
                        region: delaysByRegion.region,
                        delay: delay,
                        category: cellContext.row.original.category.id,
                        universe: defaultUniversesByRegion[delaysByRegion.region],
                      },
                    })}
                  >
                    <div className={addModifiers('alpha-pyramids-cell', [getCellModifierByCount(stats.alphaCount)])}>
                      {stats.alphaCount}
                    </div>
                  </a>
                }
              />
            );
          }

          return <div className={addModifiers('alpha-pyramids-cell', ['disabled'])}></div>;
        },
      })),
    })) as PyramidColumn<AlphaPyramid>[]
  );
}

const activationThreshold = 3;

interface PyramidsTableProps {
  startDate?: string;
  endDate?: string;
}

export function useDefaultUniverses() {
  const isAlphaOptionsLoaded = useSelector(alphasLoadedOptionsSelector) as boolean;
  const alphaSettingsOptions = useSelector(alphasOptionsSettingsChildrenSelector) as Immutable.Map<string, any>;
  const defaultUniversesByRegion = {} as Record<string, string>;

  if (!isAlphaOptionsLoaded || !alphaSettingsOptions) {
    return defaultUniversesByRegion;
  }

  const universeOptionsImmutable = alphaSettingsOptions.getIn([
    'universe',
    'choices',
    'instrumentType',
    'EQUITY',
    'region',
  ]) as Immutable.Map<string, any>;
  const universeOptions = universeOptionsImmutable.toJS() as Record<string, any>;

  Object.entries(universeOptions).forEach(([region, options]) => {
    defaultUniversesByRegion[region as string] = options[0].value;
  });
  return defaultUniversesByRegion;
}

const PyramidsTable = ({ intl, startDate, endDate }: PyramidsTableProps & InjectedIntlProps) => {
  const { data, isLoading } = useAlphaPyramid({ startDate, endDate });
  const handleOrderChange = () => {};
  const transformColumn = React.useMemo(
    () =>
      getColumnTransform<AlphaPyramid>({
        intl,
        sortOrder: { key: 'dataCategory', isAscending: false },
        handleOrderChange,
        isOrderDisabled: true,
      }),
    [intl]
  );
  const defaultUniversesByRegion = useDefaultUniverses();

  const tableColumns = React.useMemo(() => {
    if (!data) {
      return [];
    }
    return getReactTableColumns(data.regions, defaultUniversesByRegion, (scope: string) =>
      analytics.trackCustomEvent('genius_click_pyramid_alpha_distribution', { event_label: scope })
    ).map(transformColumn);
  }, [transformColumn, data]);

  const [sorting, setSorting] = useState<SortingState>([]);

  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: ['dataCategory'],
    right: [],
  });

  const table = useReactTable({
    columns: tableColumns as ColumnDef<AlphaPyramid, any>[],
    data: (data && data.data) || [],
    state: {
      sorting,
      columnPinning,
    },
    onSortingChange: setSorting,
    onColumnPinningChange: setColumnPinning,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
  });

  const headerGroups = table.getHeaderGroups();

  // show loader when data load for the first time
  if (isLoading) {
    return (
      <div className="loader">
        <Loader active={true} inline="centered" size="massive" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="agreements__content full-height">
        <div className="agreements__content-scroll full-height">
          <div className="agreements__not-available full-height">
            <FormattedMessageHelper id="genius.pyramidMultipliers.error" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="alpha-pyramids">
      <div className="agreements__content full-height">
        <div className="block-table-container">
          <div className="ReactTable block-table-wrapper -highlight">
            <div className="rt-table block-table" role="grid">
              {headerGroups.map((headerGroup, i) => (
                <div key={headerGroup.id} className={headerGroups.length === 2 && i === 0 ? 'rt-thead -headerGroups' : 'rt-thead -header'}>
                  <div
                    {...{
                      key: headerGroup.id,
                      className: 'rt-tr',
                      role: 'row',
                    }}
                  >
                    {headerGroup.headers.map(header => (
                      // eslint-disable-next-line react/jsx-key
                      <div
                        {...{
                          key: header.id,
                          className: getHeaderClassName<AlphaPyramid>(header, sorting),
                          onClick: header.column.getToggleSortingHandler(),
                          role: 'columnheader',
                          style: {
                            width: header.getSize(),
                            flex: `${header.getSize()} 0 auto`,
                            background: 'inherit',
                            ...getCommonPinningStyles(header.column),
                          },
                        }}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div
                {...{
                  className: 'rt-tbody',
                }}
              >
                {table.getRowModel().rows.map((row, i) => (
                  <div className="rt-tr-group" role="rowgroup" key={row.id}>
                    <div
                      {...{
                        key: row.id,
                        className: `rt-tr ${i % 2 === 0 ? '-odd' : '-even'}`,
                        role: 'row',
                      }}
                    >
                      {row.getVisibleCells().map(cell => (
                        // eslint-disable-next-line react/jsx-key
                        <div
                          {...{
                            key: cell.id,
                            // @ts-ignore
                            className: `${addModifiers('rt-td', [cell.column.id])} ${cell.column.columnDef.meta?.className ?? ''}`,
                            role: 'gridcell',
                            style: {
                              flex: `${cell.column.getSize()} 0 auto`,
                              width: cell.column.getSize(),
                              ...getCommonPinningStyles(cell.column),
                            },
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {getFooterGroups(table)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
